import { ChangeEventHandler, DetailedHTMLProps, InputHTMLAttributes, MouseEventHandler, forwardRef, useId } from 'react';

interface Props extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    checked?: boolean;
    classNames?: string;
    label: string;
    /** Read out (visually hidden) while the switch is aria-disabled, to say why it is unavailable. */
    disabledReason?: string;
}

export const Switch = forwardRef<HTMLInputElement, Props>(
    ({ classNames = '', checked, id, label, disabledReason, 'aria-disabled': ariaDisabled, 'aria-describedby': ariaDescribedBy, onClick, onChange, ...props }: Props, ref) => {
        const generatedId = useId();
        id ??= generatedId;
        const reasonId = `${id}-disabled-reason`;
        const isAriaDisabled = ariaDisabled === true || ariaDisabled === 'true';
        const showReason = isAriaDisabled && !!disabledReason;

        // aria-disabled keeps the switch focusable but does not stop it from toggling; Space and clicks on the
        // label reach the input as a click too, so this covers them. Preventing the click puts the checkbox back,
        // but React still reports a change for it, so the change is dropped as well.
        const handleClick: MouseEventHandler<HTMLInputElement> = (e) => {
            if (isAriaDisabled) {
                e.preventDefault();
                return;
            }
            onClick?.(e);
        };
        const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
            if (isAriaDisabled) return;
            onChange?.(e);
        };

        return (
            <div className={`flex items-start ${isAriaDisabled ? 'opacity-50' : ''} ${classNames}`}>
                <input
                    id={id}
                    type="checkbox"
                    role="switch"
                    checked={checked}
                    className="peer flex-none w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full mt-0.25em outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out aria-disabled:cursor-not-allowed"
                    {...props}
                    aria-disabled={ariaDisabled}
                    aria-describedby={[ariaDescribedBy, showReason ? reasonId : undefined].filter(Boolean).join(' ') || undefined}
                    onClick={handleClick}
                    onChange={handleChange}
                    ref={ref}
                />
                <label htmlFor={id} className="pl-[0.5em] peer-aria-disabled:cursor-not-allowed">
                    {label}
                </label>
                {showReason && (
                    <span id={reasonId} className="sr-only">
                        {disabledReason}
                    </span>
                )}
            </div>
        );
    }
);
